import { useEffect, useState } from 'react';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Titulo from '../../components/tipografia/Titulo';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import VBox from '../../components/layout/VBox';
import Grid, { GridItem } from '../../components/layout/Grid';
import Botao from '../../components/Botao';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import Badge from '../../components/layout/Badge';
import Modal from '@/components/layout/Modal';
import { mdiPlus, mdiPlay, mdiPen, mdiDelete } from '@mdi/js';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/useAuth';
import { RelatorioPersonalizadoDto } from '@tera/shared/src/dto/RelatorioPersonalizadoDto';
import { PERIODO_RELATIVO_OPTIONS } from './util';
import { REPORT_TYPES } from '../../components/Relatorios/reportTypes';
import ModalNovoRelatorio from '../../components/Relatorios/ModalNovoRelatorio';
import VisualizadorRelatorio from './view/VisualizadorRelatorio';

const Page = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [novoRelatorioAberto, setNovoRelatorioAberto] = useState(false);
    const [relatorioEmExecucao, setRelatorioEmExecucao] = useState<RelatorioPersonalizadoDto | null>(null);
    const [relatorioParaEditar, setRelatorioParaEditar] = useState<RelatorioPersonalizadoDto | null>(null);
    const [modalEditarAberto, setModalEditarAberto] = useState(false);

    const relatoriosSalvosQuery = useQuery(
        'relatorio_personalizado',
        async () => { const res = await axios.get('/api/relatorio_personalizado'); return res.data; },
        { staleTime: 30000 }
    );
    const relatoriosSalvos: RelatorioPersonalizadoDto[] = relatoriosSalvosQuery.data?.result ?? [];

    // Abre automaticamente via ?rp=<id>
    useEffect(() => {
        const rpId = searchParams.get('rp');
        if (rpId && relatoriosSalvos.length > 0) {
            const rel = relatoriosSalvos.find(r => String(r.id) === rpId);
            if (rel) setRelatorioEmExecucao(rel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relatoriosSalvos.length]);

    return (
        <LayoutUsuario>
            <VBox gap="16px">
                {/* Header */}
                <HBox>
                    <VBox style={{ flex: 1 }}>
                        <Titulo>Relatórios Personalizados</Titulo>
                        <Descricao>Relatórios personalizados salvos por você ou pela sua organização</Descricao>
                    </VBox>
                    <Botao variant="primary" icon={mdiPlus} onClick={() => setNovoRelatorioAberto(true)}>
                        Novo
                    </Botao>
                </HBox>

                {/* Lista */}
                {relatoriosSalvosQuery.isLoading ? (
                    <p style={{ color: 'var(--tc-color-gray-400)', fontSize: '0.875rem' }}>Carregando…</p>
                ) : relatoriosSalvos.length === 0 ? (
                    <VBox style={{ alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
                        <p style={{ color: 'var(--tc-color-gray-400)', fontSize: '0.95rem', margin: 0 }}>
                            Nenhum relatório personalizado salvo ainda.
                        </p>
                    </VBox>
                ) : (
                    <Grid>
                        {relatoriosSalvos.map(rel => {
                            const rt = REPORT_TYPES.find(r => r.id === rel.report_type_id);
                            const isDono = rel.usuario_id === user?.id;
                            return (
                                <GridItem key={rel.id} sm={12} md={6} lg={4}>
                                    <div style={{
                                        border: '1px solid var(--tc-color-gray-200)',
                                        borderRadius: 8,
                                        padding: 16,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 10,
                                        height: '100%',
                                    }}>
                                        <HBox style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <strong style={{ fontSize: '0.95rem', lineHeight: 1.3, flex: 1 }}>{rel.titulo}</strong>
                                            <HBox style={{ gap: 4, flexShrink: 1, flexGrow: 0, width: 'auto' }}>
                                                {isDono && (
                                                    <Botao variant="none-primary" icon={mdiPen} title="Editar"
                                                        onClick={() => { setRelatorioParaEditar(rel); setModalEditarAberto(true); }} />
                                                )}
                                                {isDono && (
                                                    <BotaoPerigoso variant="none-danger" icon={mdiDelete} title="Excluir"
                                                        onClick={async () => {
                                                            await axios.delete(`/api/relatorio_personalizado/${rel.id}`);
                                                            relatoriosSalvosQuery.refetch();
                                                        }} />
                                                )}
                                            </HBox>
                                        </HBox>
                                        <HBox style={{ gap: 6, flexWrap: 'wrap' }}>
                                            <Badge variant="secondary" style={{ fontSize: '0.75rem' }}>{rt?.label ?? rel.report_type_id}</Badge>
                                            <Badge variant={rel.escopo === 'publico' ? 'info' : 'secondary'} style={{ fontSize: '0.75rem' }}>
                                                {rel.escopo === 'publico' ? '🌐 Público' : '🔒 Privado'}
                                            </Badge>
                                            <Badge variant="secondary" style={{ fontSize: '0.75rem' }}>
                                                {PERIODO_RELATIVO_OPTIONS.find(o => o.id === rel.periodo)?.label ?? rel.periodo}
                                            </Badge>
                                        </HBox>
                                        <Botao variant="outline-primary" icon={mdiPlay}
                                            onClick={() => {
                                                setRelatorioEmExecucao(rel);
                                                setSearchParams(p => { p.set('rp', String(rel.id)); return p; });
                                            }}>
                                            Executar
                                        </Botao>
                                    </div>
                                </GridItem>
                            );
                        })}
                    </Grid>
                )}
            </VBox>

            <ModalNovoRelatorio
                show={novoRelatorioAberto || modalEditarAberto}
                onClose={() => {
                    setNovoRelatorioAberto(false);
                    setModalEditarAberto(false);
                    setRelatorioParaEditar(null);
                }}
                relatorio={relatorioParaEditar}
                onSaved={() => relatoriosSalvosQuery.refetch()}
            />

            {/* Modal de execução */}
            {relatorioEmExecucao && (() => {
                const rt = REPORT_TYPES.find(r => r.id === relatorioEmExecucao.report_type_id);
                return (
                    <Modal
                        size="lg"
                        title={relatorioEmExecucao.titulo}
                        show={!!relatorioEmExecucao}
                        hideControls
                        onClose={() => {
                            setRelatorioEmExecucao(null);
                            setSearchParams(p => { p.delete('rp'); return p; });
                        }}
                    >
                        <VisualizadorRelatorio
                            key={relatorioEmExecucao.id}
                            titulo={relatorioEmExecucao.titulo}
                            href={rt?.href}
                            metricas={relatorioEmExecucao.metricas}
                            dimensoes={relatorioEmExecucao.dimensoes}
                            filtrosBase={relatorioEmExecucao.filtros_raw}
                            periodo={relatorioEmExecucao.periodo}
                            periodoInicio={relatorioEmExecucao.periodo_inicio ?? ''}
                            periodoFim={relatorioEmExecucao.periodo_fim ?? ''}
                            csv
                        />
                    </Modal>
                );
            })()}
        </LayoutUsuario>
    );
};

export default Page;
